import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { api, ApiError, newKey } from '../lib/api';
import { useLang } from '../lib/i18n';
import { dayShort, hhmm, mmss, tl, FEE_KURUS } from '../lib/format';
import { getState, setState, useStore } from '../lib/store';
import { toast } from '../lib/toast';
import type { MapResponse } from '../lib/types';
import { ErrorView, Loading } from '../components/AppHeader';
import { Ico, Poster } from '../components/brand';
import { HOLD_TTL_MS } from '../../../src/domain/holds';

interface Card {
  card: string;
  cname: string;
  exp: string;
  cvc: string;
}
type Overlay =
  | null
  | { k: 'otp'; paymentId: string; orderId: string; last4: string; amount: number; phoneHint: string }
  | { k: 'wait' }
  | { k: 'expired' }
  | { k: 'refunded' }
  | { k: 'failed' };

const brandOf = (v: string) => (/^4/.test(v) ? 'VISA' : /^5/.test(v) ? 'MC' : /^9792/.test(v) ? 'TROY' : 'KART');

export function Checkout() {
  const { sid = '' } = useParams();
  const nav = useNavigate();
  const { tx, lang } = useLang();
  const en = lang === 'en';
  const hold = useStore((s) => (s.hold && s.hold.sessionId === sid ? s.hold : null));
  const buyer = useStore((s) => s.buyer);
  const [data, setData] = useState<MapResponse | null>(null);
  const [card, setCard] = useState<Card>({ card: '', cname: '', exp: '', cvc: '' });
  const [terms, setTerms] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [decline, setDecline] = useState<string | null>(null);
  const [ovl, setOvl] = useState<Overlay>(null);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const cdRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api<MapResponse>(`/sessions/${sid}/map`).then(setData, () => {});
  }, [sid]);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const left = hold ? hold.expiresAt - now : 0;
  useEffect(() => {
    if (hold && left <= 0 && ovl?.k !== 'wait' && ovl?.k !== 'otp') setOvl({ k: 'expired' });
  }, [hold, left, ovl]);
  useEffect(() => {
    if (cdRef.current && scrollRef.current) scrollRef.current.style.top = cdRef.current.offsetHeight + 'px';
  });
  useEffect(() => {
    if (ovl?.k === 'otp') otpRefs.current[0]?.focus();
  }, [ovl]);

  const byId = useMemo(() => new Map((data?.map.seats ?? []).map((s) => [s.id, s])), [data]);
  if (!hold) return <ErrorView msg={tx('Tutulan koltuk yok.', 'No seats are held.')} action={<button className="btn btn-primary" onClick={() => nav(`/koltuk/${sid}`)}>{tx('Koltuk seç', 'Select seats')}</button>} />;
  if (!data) return <Loading />;

  const setBuyer = (k: keyof typeof buyer, v: string | boolean) => setState((s) => ({ ...s, buyer: { ...s.buyer, [k]: v } }));
  const lines = hold.seats.map((h) => ({ ...h, seat: byId.get(h.id), price: data.prices[h.tier] }));
  const subtotal = lines.reduce((a, l) => a + l.price, 0);
  const total = subtotal + FEE_KURUS * lines.length;
  const label = (id: string) => {
    const s = byId.get(id);
    if (!s) return id;
    return s.single ? `${s.row} · ${tx('Koltuk', 'Seat')} ${s.num}` : en ? `${s.sec === 'Parter' ? 'Stalls' : s.sec === 'Balkon' ? 'Balcony' : s.sec} · Row ${s.row} · ${s.num}` : `${s.sec} · ${s.row} sırası · ${s.num}`;
  };

  const MSG: Record<string, [string, string]> = {
    name: ['Adını ve soyadını yaz', 'Enter your full name'],
    email: ['Geçerli bir e-posta adresi yaz', 'Enter a valid email address'],
    phone: ['Telefon numarası eksik', 'Phone number is incomplete'],
    card: ['Kart numarası 16 haneli olmalı', 'Card number must be 16 digits'],
    cname: ['Kart üzerindeki ismi yaz', 'Enter the name on the card'],
    exp: ['AA/YY biçiminde yaz', 'Use MM/YY'],
    cvc: ['3 haneli kod', '3-digit code'],
    terms: ['Devam etmek için sözleşmeyi onaylaman gerekiyor', 'You need to accept the terms to continue'],
  };
  const validate = () => {
    const e: Record<string, string> = {};
    if (buyer.name.trim().length < 3) e.name = 'name';
    if (!/^\S+@\S+\.\S+$/.test(buyer.email)) e.email = 'email';
    if (buyer.phone.replace(/\D/g, '').length < 10) e.phone = 'phone';
    if (card.card.replace(/\D/g, '').length !== 16) e.card = 'card';
    if (card.cname.trim().length < 3) e.cname = 'cname';
    if (!/^(0[1-9]|1[0-2])\/\d\d$/.test(card.exp)) e.exp = 'exp';
    if (!/^\d{3,4}$/.test(card.cvc)) e.cvc = 'cvc';
    if (!terms) e.terms = 'terms';
    return e;
  };

  const pay = async () => {
    const e = validate();
    setErrors(e);
    setDecline(null);
    if (Object.keys(e).length) {
      setTimeout(() => (document.querySelector('[aria-invalid="true"]') as HTMLElement | null)?.focus(), 0);
      return;
    }
    // one idempotency key per payment attempt: network retries reuse it, a new card after a decline gets a new one
    let key = getState().hold?.checkoutKey;
    if (!key) {
      key = newKey();
      setState((s) => ({ ...s, hold: s.hold ? { ...s.hold, checkoutKey: key } : s.hold }));
    }
    setBusy(true);
    const body = {
      sessionId: sid,
      holdId: hold.holdId,
      buyer: { name: buyer.name, email: buyer.email, phone: buyer.phone, sms: buyer.sms },
      card: { number: card.card, name: card.cname, exp: card.exp, cvc: card.cvc },
      terms,
      lang,
    };
    try {
      let r: { orderId: string; payment: { id: string; challenge: { last4: string; amountKurus: number; phoneHint: string } } } | null = null;
      for (let attempt = 0; attempt < 4 && !r; attempt++) {
        try {
          r = await api('/checkout', { method: 'POST', headers: { 'idempotency-key': key }, json: body });
        } catch (x) {
          const err = x as ApiError;
          if (err.status === 409 && err.body.error === 'in_progress') await new Promise((ok) => setTimeout(ok, 600));
          else if (!err.status) await new Promise((ok) => setTimeout(ok, 800)); // network blip → same key, safe to retry
          else throw x;
        }
      }
      if (!r) throw new ApiError(503, { error: 'unavailable' });
      setOvl({ k: 'otp', paymentId: r.payment.id, orderId: r.orderId, last4: r.payment.challenge.last4, amount: r.payment.challenge.amountKurus, phoneHint: r.payment.challenge.phoneHint });
    } catch (x) {
      const err = x as ApiError;
      setState((s) => ({ ...s, hold: s.hold ? { ...s.hold, checkoutKey: undefined } : s.hold }));
      if (err.status === 402) setDecline(((err.body.message as Record<string, string>) || {})[lang] || tx('Ödeme reddedildi.', 'Payment declined.'));
      else if (err.status === 409 && err.body.error === 'hold_expired') setOvl({ k: 'expired' });
      else if (err.status === 422 && err.body.fields) setErrors(err.body.fields as Record<string, string>);
      else if (err.status === 429) toast(tx('Çok fazla deneme. Bir dakika sonra tekrar dene.', 'Too many attempts. Try again in a minute.'));
      else toast(tx('Ödeme başlatılamadı. Tekrar dene.', 'Couldn’t start the payment. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const confirmOtp = async () => {
    if (ovl?.k !== 'otp') return;
    const code = otpRefs.current.map((i) => i?.value || '').join('');
    const o = ovl;
    try {
      const r = await api<{ status: string; attemptsLeft: number }>(`/fakepsp/3ds/${o.paymentId}`, { method: 'POST', json: { otp: code } });
      if (r.status === 'requires_3ds') {
        toast(tx(`Kod hatalı. Test kodu: 123456 · ${r.attemptsLeft} deneme kaldı`, `Wrong code. Test code: 123456 · ${r.attemptsLeft} tries left`), 3200);
        otpRefs.current.forEach((i) => i && (i.value = ''));
        otpRefs.current[0]?.focus();
        return;
      }
      if (r.status !== 'captured') {
        setState((s) => ({ ...s, hold: s.hold ? { ...s.hold, checkoutKey: undefined } : s.hold }));
        setOvl({ k: 'failed' });
        return;
      }
      setOvl({ k: 'wait' });
      // the browser came back from the bank → server asks the PSP itself, then finalises (webhooks may race us; fine)
      const cb = await api<{ orderId: string; status: string; accessKey?: string }>('/payments/callback', { method: 'POST', json: { paymentId: o.paymentId } });
      if (cb.status === 'paid') {
        setState((s) => ({ ...s, hold: null, selection: { ...s.selection, [sid]: [] } }));
        nav(`/siparis/${cb.orderId}${cb.accessKey ? `?k=${cb.accessKey}` : ''}`, { replace: true });
      } else if (cb.status === 'refunded') {
        setState((s) => ({ ...s, hold: null, selection: { ...s.selection, [sid]: [] } }));
        setOvl({ k: 'refunded' });
      } else setOvl({ k: 'failed' });
    } catch {
      toast(tx('Bağlantı sorunu. Tekrar dene.', 'Connection problem. Try again.'));
    }
  };
  const cancelOtp = async () => {
    if (ovl?.k === 'otp') await api(`/checkout/${ovl.orderId}/cancel`, { method: 'POST' }).catch(() => {});
    setState((s) => ({ ...s, hold: s.hold ? { ...s.hold, checkoutKey: undefined } : s.hold }));
    setOvl(null);
  };
  const backToMap = () => {
    setState((s) => ({ ...s, hold: null }));
    nav(`/koltuk/${sid}`);
  };

  const field = (k: 'name' | 'email' | 'phone', lab: string, attrs: React.InputHTMLAttributes<HTMLInputElement>, hint?: string) => (
    <div className="field">
      <label htmlFor={`f-${k}`}>{lab}</label>
      <input
        className={`input ${errors[k] ? 'err' : ''}`}
        id={`f-${k}`}
        value={buyer[k]}
        onChange={(e) => setBuyer(k, e.target.value)}
        aria-invalid={errors[k] ? true : undefined}
        aria-describedby={errors[k] ? `e-${k}` : undefined}
        {...attrs}
      />
      {errors[k] ? (
        <span className="err-msg" id={`e-${k}`}>
          {MSG[k]![en ? 1 : 0]}
        </span>
      ) : hint ? (
        <span className="hint">{hint}</span>
      ) : null}
    </div>
  );
  const cfield = (k: 'cname' | 'exp' | 'cvc', lab: string, attrs: React.InputHTMLAttributes<HTMLInputElement>, fmt?: (v: string) => string) => (
    <div className="field">
      <label htmlFor={`f-${k}`}>{lab}</label>
      <input
        className={`input ${k !== 'cname' ? 'mono' : ''} ${errors[k] ? 'err' : ''}`}
        id={`f-${k}`}
        value={card[k]}
        onChange={(e) => setCard({ ...card, [k]: fmt ? fmt(e.target.value) : e.target.value })}
        aria-invalid={errors[k] ? true : undefined}
        aria-describedby={errors[k] ? `e-${k}` : undefined}
        {...attrs}
      />
      {errors[k] && (
        <span className="err-msg" id={`e-${k}`}>
          {MSG[k]![en ? 1 : 0]}
        </span>
      )}
    </div>
  );

  const pct = Math.max(0, left / HOLD_TTL_MS) * 100;
  return (
    <>
      <div className={`countdown ${left < 60000 ? 'low' : ''}`} ref={cdRef} role="timer" aria-live="off">
        <Ico n="clock" />
        <div className="txt">
          {tx('Koltukların ', 'Your seats are held for ')}
          <b>
            <span className="clock">{mmss(left)}</span>
          </b>
          {tx(' boyunca senin.', '.')}
          <br />
          <span style={{ opacity: 0.8, fontSize: 12.5 }}>{tx('Süre dolarsa koltuklar tekrar satışa açılır.', 'If time runs out, the seats go back on sale.')}</span>
        </div>
        <i className="bar" style={{ width: `${Math.min(100, pct)}%` }} />
        <span className="sr" aria-live="polite">
          {Math.ceil(left / 60000) !== Math.ceil((left + 1000) / 60000) ? tx(`${Math.ceil(left / 60000)} dakika kaldı`, `${Math.ceil(left / 60000)} minutes left`) : ''}
        </span>
      </div>
      <div className="scroll" ref={scrollRef} style={{ top: 66 }}>
        <div className="co-grid">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button type="button" className="icon-btn" onClick={() => nav(`/koltuk/${sid}`)} aria-label={tx('Koltuklara dön', 'Back to seats')}>
                <Ico n="back" />
              </button>
              <div className="steps">
                <span className="st done">
                  <span className="n">
                    <Ico n="check" cls="ico-s" />
                  </span>
                  {tx('Koltuk', 'Seats')}
                </span>
                <span className="ln" />
                <span className="st cur">
                  <span className="n">2</span>
                  {tx('Bilgiler ve ödeme', 'Details & payment')}
                </span>
                <span className="ln" />
                <span className="st">
                  <span className="n">3</span>
                  {tx('Bilet', 'Ticket')}
                </span>
              </div>
            </div>
            <h1 className="d-l" style={{ margin: '4px 0 0' }}>
              {tx('Neredeyse', 'Almost')}
              <br />
              {tx('bitti.', 'there.')}
            </h1>
            <section className="panel" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <h2 className="d-s" style={{ margin: 0 }}>
                {tx('Biletler kime gitsin?', 'Who are the tickets for?')}
              </h2>
              {field('name', tx('Ad soyad', 'Full name'), { autoComplete: 'name', placeholder: tx('Adın ve soyadın', 'Your name') })}
              {field('email', tx('E-posta', 'Email'), { type: 'email', autoComplete: 'email', placeholder: 'ornek@posta.com' }, tx('Biletlerin ve fatura bu adrese gelir.', 'Tickets and the receipt go to this address.'))}
              {field('phone', tx('Cep telefonu', 'Mobile phone'), { type: 'tel', autoComplete: 'tel', placeholder: '+90 5__ ___ __ __' })}
              <label className="check">
                <input type="checkbox" checked={buyer.sms} onChange={(e) => setBuyer('sms', e.target.checked)} /> {tx('Biletleri SMS ile de gönder', 'Also send tickets by SMS')}
              </label>
            </section>
            <section className="panel" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <h2 className="d-s" style={{ margin: 0 }}>
                  {tx('Kartla öde', 'Pay by card')}
                </h2>
                <span className="t-s muted" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <Ico n="lock" cls="ico-s" /> 3-D Secure
                </span>
              </div>
              <div className="testnote">
                <Ico n="info" cls="ico-s" />
                <div>
                  <b>{tx('Test modu.', 'Test mode.')}</b> {tx('Gerçek ödeme alınmaz. Kart:', 'No real charge. Card:')} <b>4242 4242 4242 4242</b>, {tx('son kullanma: ileri bir tarih, CVC:', 'expiry: any future date, CVC:')} <b>123</b>.{' '}
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    style={{ padding: '0 6px', minHeight: 28, textDecoration: 'underline' }}
                    onClick={() => {
                      setCard({ card: '4242 4242 4242 4242', cname: (buyer.name || 'MELIH KIZMAZ').toLocaleUpperCase('tr-TR'), exp: '12/29', cvc: '123' });
                      setErrors({});
                    }}
                  >
                    {tx('Test kartını doldur', 'Fill test card')}
                  </button>
                  <br />
                  <span style={{ opacity: 0.85 }}>
                    {tx('Red senaryosu:', 'Decline scenario:')} <b>4000 0000 0000 0002</b> · {tx('yetersiz bakiye:', 'insufficient funds:')} <b>4000 0000 0000 9995</b>
                  </span>
                </div>
              </div>
              {decline && (
                <div className="err-banner" role="alert">
                  <Ico n="info" cls="ico-s" />
                  <div>
                    <b>{decline}</b>
                    <div>{tx('Koltukların hâlâ senin; başka bir kartla deneyebilirsin.', 'Your seats are still held; you can try another card.')}</div>
                  </div>
                </div>
              )}
              <div className="field">
                <label htmlFor="f-card">{tx('Kart numarası', 'Card number')}</label>
                <div className="cardnum">
                  <input
                    className={`input mono ${errors.card ? 'err' : ''}`}
                    id="f-card"
                    inputMode="numeric"
                    autoComplete="cc-number"
                    placeholder="0000 0000 0000 0000"
                    maxLength={19}
                    value={card.card}
                    aria-invalid={errors.card ? true : undefined}
                    aria-describedby={errors.card ? 'e-card' : undefined}
                    onChange={(e) =>
                      setCard({
                        ...card,
                        card: e.target.value
                          .replace(/\D/g, '')
                          .slice(0, 16)
                          .replace(/(.{4})(?=.)/g, '$1 '),
                      })
                    }
                  />
                  <span className="brand">{brandOf(card.card)}</span>
                </div>
                {errors.card && (
                  <span className="err-msg" id="e-card">
                    {MSG.card![en ? 1 : 0]}
                  </span>
                )}
              </div>
              {cfield('cname', tx('Kart üzerindeki isim', 'Name on card'), { autoComplete: 'cc-name' })}
              <div className="grid2">
                {cfield('exp', tx('Son kullanma', 'Expiry'), { inputMode: 'numeric', autoComplete: 'cc-exp', placeholder: tx('AA/YY', 'MM/YY'), maxLength: 5 }, (v) => {
                  const d = v.replace(/\D/g, '').slice(0, 4);
                  return d.length > 2 ? d.slice(0, 2) + '/' + d.slice(2) : d;
                })}
                {cfield('cvc', 'CVC', { inputMode: 'numeric', autoComplete: 'cc-csc', placeholder: '123', maxLength: 4 }, (v) => v.replace(/\D/g, '').slice(0, 4))}
              </div>
            </section>
            <label className="check">
              <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} aria-invalid={errors.terms ? true : undefined} />{' '}
              <span>
                {tx('Ön bilgilendirme formunu ve mesafeli satış sözleşmesini okudum, kabul ediyorum.', 'I have read and accept the pre-information form and the distance sales agreement.')}
                {errors.terms && (
                  <>
                    <br />
                    <span className="err-msg">{MSG.terms![en ? 1 : 0]}</span>
                  </>
                )}
              </span>
            </label>
            <button type="button" className="btn btn-primary btn-lg btn-block" onClick={pay} disabled={busy}>
              <Ico n="lock" cls="ico-s" /> {busy ? tx('Bankaya bağlanılıyor…', 'Contacting your bank…') : en ? `Pay ${tl(total, 'en')}` : `${tl(total)} öde`}
            </button>
            <p className="t-s muted" style={{ margin: '-8px 0 0', textAlign: 'center' }}>
              {tx('Ödemen bankanın doğrulama sayfasında onaylanacak.', 'You’ll confirm the payment on your bank’s verification page.')}
            </p>
          </div>
          <div className="panel co-sum">
            <div className="mini-ev">
              <Poster poster={data.event.poster} title={data.event.short || data.event.title} />
              <div>
                <div className="cap muted">
                  {dayShort(data.session.startsAt, lang)} · {hhmm(data.session.startsAt)}
                </div>
                <div className="d-s" style={{ marginTop: 2 }}>
                  {data.event.title}
                </div>
                <div className="t-s muted">
                  {data.venue.name}, {data.venue.area}
                </div>
              </div>
            </div>
            <hr className="rule" style={{ margin: '16px 0 8px' }} />
            {lines.map((l) => (
              <div className="line" key={l.id}>
                <span>{label(l.id)}</span>
                <span className="num">{tl(l.price, lang)}</span>
              </div>
            ))}
            <div className="line muted">
              <span>
                {tx('Hizmet bedeli', 'Service fee')} ({lines.length} × {tl(FEE_KURUS, lang)})
              </span>
              <span className="num">{tl(FEE_KURUS * lines.length, lang)}</span>
            </div>
            <div className="line big">
              <span>{tx('Toplam', 'Total')}</span>
              <span className="num">{tl(total, lang)}</span>
            </div>
            <p className="t-s muted" style={{ margin: '10px 0 0' }}>
              {tx('Fiyatlara KDV dahildir. Etkinlik iptal edilirse ücretin 14 gün içinde kartına iade edilir.', 'Prices include VAT. If the event is cancelled you’ll be refunded to your card within 14 days.')}
            </p>
          </div>
        </div>
      </div>

      {ovl?.k === 'otp' && (
        <div className="overlay">
          <div className="sheet" role="dialog" aria-modal="true" aria-labelledby="tds-t" style={{ padding: '16px 22px 30px' }}>
            <div className="bank-head">
              <span className="bank-logo">Lodos Bank</span>
              <span className="badge">3-D Secure · test</span>
            </div>
            <h2 className="d-s" id="tds-t" style={{ margin: '0 0 4px' }}>
              {tx('Ödemeni onayla', 'Confirm your payment')}
            </h2>
            <p className="t-s muted" style={{ margin: '0 0 6px' }}>
              {en ? (
                <>
                  <b style={{ color: 'var(--ink)' }}>{tl(ovl.amount, 'en')}</b> will be charged to your card ending •••• {ovl.last4}. Merchant: GİŞE BİLET A.Ş.
                </>
              ) : (
                <>
                  •••• {ovl.last4} numaralı kartından <b style={{ color: 'var(--ink)' }}>{tl(ovl.amount)}</b> çekilecek. İşyeri: GİŞE BİLET A.Ş.
                </>
              )}
            </p>
            <p className="t-s" style={{ margin: 0 }}>
              {tx(`+90 5•• ••• •• ${ovl.phoneHint} numarasına gelen 6 haneli kodu gir.`, `Enter the 6-digit code sent to +90 5•• ••• •• ${ovl.phoneHint}.`)}
            </p>
            <div className="otp">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <input
                  key={i}
                  ref={(el) => {
                    otpRefs.current[i] = el;
                  }}
                  inputMode="numeric"
                  maxLength={1}
                  aria-label={tx(`Kod hanesi ${i + 1}`, `Code digit ${i + 1}`)}
                  onChange={(e) => {
                    e.target.value = e.target.value.replace(/\D/g, '');
                    if (e.target.value && otpRefs.current[i + 1]) otpRefs.current[i + 1]!.focus();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Backspace' && !(e.target as HTMLInputElement).value && otpRefs.current[i - 1]) otpRefs.current[i - 1]!.focus();
                    if (e.key === 'Enter') confirmOtp();
                  }}
                  onPaste={(e) => {
                    const t = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                    if (t.length === 6) {
                      e.preventDefault();
                      otpRefs.current.forEach((el, k) => el && (el.value = t[k]!));
                    }
                  }}
                />
              ))}
            </div>
            <p className="t-s muted" style={{ textAlign: 'center', margin: '0 0 14px' }}>
              {tx('Test kodu', 'Test code')}{' '}
              <b className="mono" style={{ color: 'var(--ink)' }}>
                123456
              </b>{' '}
              ·{' '}
              <button type="button" className="btn btn-ghost btn-sm" style={{ minHeight: 28, padding: '0 6px', textDecoration: 'underline' }} onClick={() => otpRefs.current.forEach((el, k) => el && (el.value = '123456'[k]!))}>
                {tx('doldur', 'fill')}
              </button>
            </p>
            <button type="button" className="btn btn-ink btn-block" onClick={confirmOtp}>
              {tx('Onayla', 'Confirm')}
            </button>
            <button type="button" className="btn btn-ghost btn-block" style={{ marginTop: 6 }} onClick={cancelOtp}>
              {tx('İptal et, ödeme sayfasına dön', 'Cancel, back to payment')}
            </button>
          </div>
        </div>
      )}
      {ovl?.k === 'wait' && (
        <div className="overlay">
          <div className="sheet" role="dialog" aria-live="polite" style={{ textAlign: 'center', padding: '36px 24px 40px' }}>
            <div className="spinner" />
            <div className="d-s" style={{ marginTop: 18 }}>
              {tx('Bankandan onay bekleniyor', 'Waiting for your bank')}
            </div>
            <p className="t-s muted" style={{ margin: '6px 0 0' }}>
              {tx('Bu birkaç saniye sürebilir. Sayfayı kapatma.', 'This can take a few seconds. Don’t close the page.')}
            </p>
          </div>
        </div>
      )}
      {(ovl?.k === 'expired' || ovl?.k === 'refunded' || ovl?.k === 'failed') && (
        <div className="overlay">
          <div className="sheet" role="alertdialog" aria-labelledby="exp-t" style={{ textAlign: 'center', padding: '28px 24px 34px' }}>
            <div className="d-m" id="exp-t">
              {ovl.k === 'expired' ? tx('Süre doldu', 'Time’s up') : ovl.k === 'refunded' ? tx('Koltuklar başkasına geçti', 'The seats went to someone else') : tx('Doğrulama tamamlanmadı', 'Verification failed')}
            </div>
            <p className="muted" style={{ margin: '8px 0 18px' }}>
              {ovl.k === 'expired'
                ? tx(
                    'Koltuklarını 7 dakika tuttuk ama ödeme tamamlanmadı, bu yüzden tekrar satışa açıldılar. Aynı koltuklar hâlâ boşsa yeniden seçebilirsin.',
                    'We held your seats for 7 minutes but payment wasn’t completed, so they went back on sale. If they’re still free you can pick them again.',
                  )
                : ovl.k === 'refunded'
                  ? tx('Ödemen bankadan onaylandığında süre dolmuş ve koltuklar satılmıştı. Ücretin otomatik olarak kartına iade edildi.', 'By the time your bank confirmed, your hold had expired and the seats were sold. Your payment was refunded automatically.')
                  : tx('Banka doğrulaması başarısız oldu. Koltukların hâlâ tutuluyor olabilir; tekrar deneyebilirsin.', 'Bank verification failed. Your seats may still be held; you can try again.')}
            </p>
            {ovl.k === 'failed' ? (
              <button type="button" className="btn btn-primary btn-block" onClick={() => setOvl(null)}>
                {tx('Ödeme sayfasına dön', 'Back to payment')}
              </button>
            ) : (
              <button type="button" className="btn btn-primary btn-block" onClick={backToMap}>
                {tx('Koltuk haritasına dön', 'Back to the seat map')}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
